import { useCallback, useState } from 'react';
import { downloadBlob } from '../api/files';

export type RunStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * State and flow shared by the upload-and-download automation panels.
 *
 * `run(task, failure)` sets the loading state, awaits the API call, downloads the generated
 * file, and stores the rest of the result (filename, summary, ...) for the panel to show.
 * Any error becomes `error`, using `failure` when it carries no message of its own.
 * `fail(message)` reports a validation problem without calling the API; `reset()` returns to idle.
 */
export function useFileRun<T extends { blob: Blob; filename: string }>() {
  const [status, setStatus] = useState<RunStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Omit<T, 'blob'> | null>(null);

  const fail = useCallback((message: string) => {
    setError(message);
    setStatus('error');
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setResult(null);
  }, []);

  const run = useCallback(
    async (task: () => Promise<T>, failure: string) => {
      setStatus('loading');
      setError(null);
      setResult(null);
      try {
        const response = await task();
        const { blob, ...rest } = response;
        downloadBlob(blob, response.filename);
        setResult(rest);
        setStatus('success');
      } catch (err) {
        fail(err instanceof Error ? err.message : failure);
      }
    },
    [fail],
  );

  return { status, error, result, fail, reset, run };
}
